import { useContext, useMemo, useState } from 'react';
import { Button, Dropdown } from '@teo/components';
import { Trash, MoreVertical, ChevronRight } from '@teo/components/icons';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { encode, decode } from '@/base64';
import { copyToClipboard, orderWidgets, newUid, deep_copy } from '@/util';
import { setShowNotification } from '@/App';
import { AuthContext } from '@/components/Contexts/AuthContext';

const WidgetOptions = ({
  widget,
  placeholder,
  state,
  undo,
  chatGPT,
  delItem,
}) => {
  const { t } = useTranslation();

  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;
  const useTeoAi = authUser?.useTeoAi;

  const [compatibleGroups, setCompatibleGroups] = useState([]);
  const [openSub, setOpenSub] = useState(false);

  let groupType;
  switch (state.type) {
    case 'InvulVraag':
      groupType = 'InvulVraagGroup';
      break;
    case 'MeerKeuze':
      groupType = 'MeerKeuzeGroup';
      break;
    case 'OpenVraag':
      groupType = 'OpenVraagGroup';
      break;
    case 'NameImage2':
      groupType = 'NameImage2Group';
      break;
    case 'MatrixVraag2':
      groupType = 'MatrixVraag2Group';
      break;
    case 'OrderVraag':
      groupType = 'OrderVraagGroup';
      break;
    case 'SortGroups':
      groupType = 'SortGroupsGroup';
      break;
    case 'ShowOnPicture':
      groupType = 'ShowOnPictureGroup';
      break;
    case 'Link':
      groupType = 'LinkGroup';
      break;
    case 'FileUpload':
      groupType = 'FileUploadGroup';
      break;
  }

  const readWidgetState = (ph) => {
    if (!ph) return null;

    const ds = ph.querySelector?.('[data-state]')?.getAttribute?.('data-state');
    if (ds) {
      try {
        return JSON.parse(ds);
      } catch {
        try {
          return JSON.parse(decode(ds));
        } catch {
          // ignore
        }
      }
    }

    return ph.state || null;
  };

  const editorRoot =
    placeholder?.closest?.('.fr-element') ||
    placeholder?.parentElement ||
    document;

  const handleOpenChat = () => {
    const openChat = document.getElementById(`${state.uid}_gpt_open`);
    openChat?.click();
  };

  const handleOpenCompetencies = () => {
    document.body.style.overflow = 'hidden';
    const openCompetencies = document.getElementById(
      `${state.uid}_competencies`
    );
    openCompetencies?.click();
  };

  const handeleAddList = () => {
    if (
      state?.type == 'KofferVraag2' ||
      state?.type == 'BoxError' ||
      state?.type == 'KofferVraag' ||
      state?.group ||
      state?.isGroup
    )
      return [];

    const editorRoot =
      placeholder?.closest?.('.fr-element') ||
      placeholder?.parentElement ||
      document;

    const placeholders = Array.from(
      editorRoot.querySelectorAll?.('.widget-placeholder') || []
    );

    const groupsByUid = new Map();

    placeholders.forEach((ph) => {
      const st = readWidgetState(ph);
      if (!st) return;

      if (!st?.uid || st.uid === state.uid) return;
      if (!st?.isGroup) return;
      if (st.type !== groupType) return;

      const title =
        st?.titleWidget || t('widgets.group_widget.random_question_group');

      groupsByUid.set(st.uid, { uid: st.uid, title });
    });

    setCompatibleGroups(Array.from(groupsByUid.values())); // CHANGED
  };

  const handleConvertToGroup = async () => {
    if (
      state?.type == 'Math' ||
      state?.type == 'TabbedContentBoxes' ||
      state?.type == 'ImageShowParts' ||
      state?.type == 'Instruction'
    )
      return;

    const current = widget?.stateFn ? await widget.stateFn() : deep_copy(state);

    const baseTitle = t('widgets.group_widget.random_question_group');
    const nextGroupNumber = (() => {
      const placeholders = Array.from(
        editorRoot.querySelectorAll?.('.widget-placeholder') || []
      );
      const existingGroups = placeholders
        .map((ph) => readWidgetState(ph))
        .filter((st) => st?.isGroup);
      return existingGroups.length + 1;
    })();
    const defaultGroupTitle = `${baseTitle} ${nextGroupNumber}`;

    const groupState = {
      type: groupType,
      uid: newUid(20),
      items: [deep_copy(current)],
      showCount: 1,
      randomPick: true,
      score: current?.score,
      oneAnswer: current?.oneAnswer,
      randomOrder: current?.randomOrder,
      excludeScore: current?.excludeScore,
      isGroup: true,
      titleWidget: defaultGroupTitle,
    };

    window.dispatchEvent(
      new CustomEvent('froala:replace-widget-state', {
        detail: { oldUid: state.uid, nextState: groupState },
      })
    );
  };

  const handleAddToGroup = (groupUid) => {
    if (!groupUid) return;
    window.dispatchEvent(
      new CustomEvent('froala:move-widget-to-group', {
        detail: { widgetUid: state.uid, groupUid },
      })
    );
  };

  return (
    <>
      {state?.type !== 'ImageShowParts' &&
        state?.type !== 'TabbedContentBoxes' && (
          <>
            {state?.type !== 'Math' &&
              state?.type !== 'TabbedContentBoxes' &&
              state?.type !== 'ImageShowParts' &&
              state?.type !== 'Instruction' &&
              state?.type !== 'BoxError' &&
              state?.type !== 'KofferVraag2' &&
              state?.type !== 'KofferVraag' &&
              !state?.isGroup &&
              !state?.group && (
                <Button
                  onClick={handleConvertToGroup}
                  variant="ghost"
                  className="flex text-grey-01"
                >
                  <img
                    src="/images/icons/group.svg"
                    alt="create group"
                    className="w-5"
                  />
                </Button>
              )}

            {state?.type !== 'Math' && !state?.group && (
              <Dropdown>
                <Dropdown.Trigger>
                  <div
                    className="rounded-md px-4 py-2 hover:bg-grey-transparent-01"
                    onClick={() => {
                      setOpenSub(false);
                      handeleAddList();
                    }}
                  >
                    <MoreVertical className="h-5 w-5" />
                  </div>
                </Dropdown.Trigger>

                <Dropdown.Content style={{ top: '25px' }}>
                  <Dropdown.Section className="">
                    <p className="text-xs text-grey-06">
                      {t('widgets.widget_competencies.institutions')}
                    </p>
                    <Dropdown.Actions>
                      <Dropdown.Button onClick={handleOpenCompetencies}>
                        <p className="text-sm">
                          {t('widgets.widget_competencies.edit_competencies')}
                        </p>
                      </Dropdown.Button>
                    </Dropdown.Actions>
                  </Dropdown.Section>
                  {state?.type !== 'TabbedContentBoxes' &&
                    state?.type !== 'ImageShowParts' &&
                    state?.type !== 'Instruction' &&
                    state?.type !== 'BoxError' &&
                    state?.type !== 'KofferVraag2' &&
                    state?.type !== 'KofferVraag' &&
                    !state?.isGroup && (
                      <Dropdown.Section className="">
                        <div className="relative">
                          <div
                            className={`flex flex-row items-center rounded-md p-3 text-sm hover:bg-grey-01 ${
                              compatibleGroups?.length < 1 && 'text-grey-06'
                            }`}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setOpenSub(!openSub);
                            }}
                          >
                            {t('widgets.group_widget.add_to')}
                            <ChevronRight className="ml-auto h-5 w-5 text-grey-05" />
                          </div>
                          {compatibleGroups?.length > 0 && openSub && (
                            <div className="absolute right-[110%] top-0 w-max rounded-lg bg-white px-2 py-1">
                              <Dropdown.Actions>
                                {compatibleGroups.map((g) => (
                                  <Dropdown.Button
                                    key={g.uid}
                                    onClick={() => handleAddToGroup(g.uid)}
                                    className="!h-10"
                                  >
                                    <p className="text-sm">{g.title}</p>
                                  </Dropdown.Button>
                                ))}
                              </Dropdown.Actions>
                            </div>
                          )}
                        </div>
                      </Dropdown.Section>
                    )}
                </Dropdown.Content>
              </Dropdown>
            )}

            {!state?.group && (
              <Button
                Icon={Trash}
                onClick={async () => {
                  const st = await widget.stateFn();
                  st.uid = newUid(20);
                  const stateStr = 'widget:' + encode(JSON.stringify(st));
                  copyToClipboard(stateStr);
                  setShowNotification(i18n.t('notifications.widget_copied'));
                }}
                variant="ghost"
                className="flex text-grey-01"
              >
                <img
                  src="/images/icons/copy.svg"
                  alt="copy button"
                  className="w-5"
                />
              </Button>
            )}
          </>
        )}

      {chatGPT && useTeoAi && (
        <Button
          onClick={handleOpenChat}
          variant="ghost"
          className="flex text-grey-01"
        >
          <img src="/images/icons/chatgpt.svg" alt="chatgpt" className="w-5" />
        </Button>
      )}

      <Button
        Icon={Trash}
        onClick={() => {
          if (state?.group) {
            delItem();
          } else {
            placeholder.remove();
            orderWidgets();
            undo?.reset?.();
          }
        }}
        variant="ghost"
        className="flex text-grey-01"
      >
        <img
          src="/images/icons/trash_icon_red.svg"
          alt="delete button"
          className="w-5"
        />
      </Button>
    </>
  );
};

export { WidgetOptions };
